import { defineStore } from "pinia";
import { rappelService } from "@/api/services/rappelService";

export const useRappelStore = defineStore("rappel", {
  state: () => ({
    groupedReminders: {
      totalItems: 0,
      proprietaires: [],
    },

    loading: false,
    error: null,

    showCreateModal: false,
    showEditModal: false,
    editingReminder: null,

    reminderForm: {
      ownerId: null,
      productVariantId: null,
      rationJournaliere: "",
      dateMiseEnPlace: "",
      notificationType: [],
    },

    formErrors: {},
    searchTerm: "",
  }),

  getters: {
    hasReminders: state => {
      return state.groupedReminders.proprietaires.length > 0;
    },

    totalReminders: state => {
      return state.groupedReminders.totalItems;
    },

    ownersWithReminders: state => {
      return state.groupedReminders.proprietaires || [];
    },

    isLoading: state => {
      return state.loading;
    },

    currentError: state => {
      return state.error;
    },

    isFormValid: state => {
      return Object.keys(state.formErrors).length === 0;
    },
  },

  actions: {
    async fetchGroupedReminders() {
      try {
        this.loading = true;
        this.error = null;
        this.searchTerm = "";

        const data = await rappelService.getGroupedRemindersByOwners();
        this.groupedReminders = data;
      } catch (error) {
        console.error("Erreur lors du chargement des rappels:", error);
        this.error = "Impossible de charger les rappels. Veuillez réessayer.";

        this.groupedReminders = {
          totalItems: 0,
          proprietaires: [],
        };
      } finally {
        this.loading = false;
      }
    },

    async createReminder(reminderData) {
      try {
        this.loading = true;
        this.error = null;

        const validation = rappelService.validateReminderData(reminderData);
        if (!validation.isValid) {
          this.formErrors = validation.errors;
          return false;
        }

        await rappelService.createReminder(reminderData);

        await this.fetchGroupedReminders();

        this.resetForm();
        this.showCreateModal = false;

        return true;
      } catch (error) {
        console.error("Erreur lors de la création du rappel:", error);
        this.error = "Erreur lors de la création du rappel. Veuillez réessayer.";

        if (error.response?.status === 400) {
          this.error = "Données invalides. Vérifiez les informations saisies.";
        } else if (error.response?.status === 404) {
          this.error = "Propriétaire ou produit introuvable.";
        }

        return false;
      } finally {
        this.loading = false;
      }
    },

    async updateReminder(id, updateData) {
      try {
        this.loading = true;
        this.error = null;

        const validation = rappelService.validateReminderData(updateData);
        if (!validation.isValid) {
          this.formErrors = validation.errors;
          return false;
        }

        await rappelService.updateReminder(id, updateData);

        await this.fetchGroupedReminders();

        this.resetForm();
        this.showEditModal = false;
        this.editingReminder = null;

        return true;
      } catch (error) {
        console.error("Erreur lors de la mise à jour du rappel:", error);
        this.error = "Erreur lors de la modification du rappel. Veuillez réessayer.";
        return false;
      } finally {
        this.loading = false;
      }
    },

    async deleteReminder(id) {
      try {
        this.loading = true;
        this.error = null;

        await rappelService.deleteReminder(id);

        await this.fetchGroupedReminders();

        return true;
      } catch (error) {
        console.error("Erreur lors de la suppression du rappel:", error);
        this.error = "Erreur lors de la suppression du rappel. Veuillez réessayer.";

        if (error.response?.status === 404) {
          this.error = "Ce rappel n'existe plus ou a déjà été supprimé.";
        } else if (error.response?.status === 403) {
          this.error = "Vous n'avez pas les droits pour supprimer ce rappel.";
        }

        return false;
      } finally {
        this.loading = false;
      }
    },

    async prepareEdit(reminder) {
      try {
        this.error = null;

        this.reminderForm = {
          ownerId: reminder.ownerId,
          productVariantId: reminder.productVariantId,
          rationJournaliere: reminder.rationJournaliere,
          dateMiseEnPlace: reminder.dateMiseEnPlace ? reminder.dateMiseEnPlace.split(" ")[0] : "",
          notificationType: [...(reminder.notificationType || [])],
        };

        this.editingReminder = reminder;
        this.showEditModal = true;
        this.formErrors = {};
      } catch (error) {
        console.error("Erreur lors de la préparation de l'édition:", error);
        this.error = "Impossible de préparer l'édition du rappel.";
      }
    },

    openCreateModal() {
      this.resetForm();
      this.showCreateModal = true;
      this.formErrors = {};
    },

    closeModals() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.editingReminder = null;
      this.resetForm();
    },

    resetForm() {
      this.reminderForm = {
        ownerId: null,
        productVariantId: null,
        rationJournaliere: "",
        dateMiseEnPlace: "",
        notificationType: [],
      };
      this.formErrors = {};
    },

    updateFormField(field, value) {
      this.reminderForm[field] = value;

      if (this.formErrors[field]) {
        delete this.formErrors[field];
      }
    },

    clearError() {
      this.error = null;
    },

    $reset() {
      this.groupedReminders = {
        totalItems: 0,
        proprietaires: [],
      };
      this.loading = false;
      this.error = null;
      this.showCreateModal = false;
      this.showEditModal = false;
      this.editingReminder = null;
      this.resetForm();
      this.searchTerm = "";
    },
  },
});
